import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import { revalidatePublicPath } from '../../utils/revalidatePublic';
import MediaKitForm from './MediaKitForm';
import MediaKitSectionPreview from './MediaKitSectionPreview';

const PUBLIC_PATHS = { es: '/es/publicidad', en: '/en/advertise' };

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function answerHtml(faq) {
  if (faq?.answer_html) return faq.answer_html;
  const plain = String(faq?.answer || '').trim();
  if (!plain) return '';
  return `<p>${escapeHtml(plain).replace(/\n/g, '<br />')}</p>`;
}

function hydrateLang(lang) {
  return {
    ...lang,
    faqs: {
      ...(lang?.faqs || {}),
      items: (lang?.faqs?.items || []).map((faq) => {
        const html = answerHtml(faq);
        return {
          question: faq.question || '',
          answer: faq.answer || '',
          answer_html: html,
          answer_html_updated: html,
        };
      }),
    },
  };
}

function serializeLang(lang) {
  return {
    ...lang,
    faqs: {
      ...lang.faqs,
      items: (lang.faqs?.items || []).map((faq) => ({
        question: faq.question || '',
        answer: faq.answer || '',
        answer_html: faq.answer_html_updated || faq.answer_html || '',
      })),
    },
    packages: {
      ...lang.packages,
      items: (lang.packages?.items || []).map((plan) => ({
        ...plan,
        includes: (plan.includes || []).map((line) => String(line)).filter((line) => line.trim()),
      })),
    },
  };
}

function splitMetric(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) return { value: raw, unit: '' };
  const text = String(raw ?? '').trim();
  const match = text.match(/^(\d+(?:[.,]\d+)?)\s*(.*)$/);
  if (!match) return { value: '', unit: '' };
  const value = Number(match[1].replace(',', '.'));
  if (!Number.isFinite(value)) return { value: '', unit: '' };
  return { value, unit: match[2].trim() };
}

function deriveShared(es, en) {
  const src = es || en || {};
  return {
    pdf_url: src.hero?.pdf_url || '',
    email: src.contact?.email || en?.contact?.email || '',
    whatsapp: src.contact?.whatsapp || en?.contact?.whatsapp || '+52 686 400 2234',
    metrics: (src.audience?.metrics || []).map((item) => ({ value: splitMetric(item?.value).value })),
    countries: (src.profile?.countries || []).map((item) => ({ percent: Number(item?.percent) || 0 })),
    cities: (src.profile?.cities || []).map((item) => ({ percent: Number(item?.percent) || 0 })),
    formats: (src.formats?.items || []).map((item) => ({ muted: Boolean(item?.muted) })),
    packages: (src.packages?.items || []).map((item) => ({
      price: item?.price || '',
      featured: Boolean(item?.featured),
      cta_url: item?.cta_url || '',
    })),
    steps: (src.process?.steps || []).map((item) => ({ number: item?.number || '' })),
    socials: (src.contact?.socials || en?.contact?.socials || []).map((item) => ({
      platform: item?.platform || 'other',
      label: item?.label || '',
      url: item?.url || '',
    })),
  };
}

function applyShared(lang, shared) {
  const next = JSON.parse(JSON.stringify(lang || {}));
  next.hero = next.hero || {};
  next.contact = next.contact || {};
  next.audience = next.audience || { metrics: [] };
  next.profile = next.profile || { countries: [], cities: [] };
  next.formats = next.formats || { items: [] };
  next.packages = next.packages || { items: [] };
  next.process = next.process || { steps: [] };
  next.hero.pdf_url = shared.pdf_url || '';
  next.contact.email = shared.email || '';
  next.contact.whatsapp = shared.whatsapp || '';
  next.audience.metrics = (next.audience.metrics || []).map((metric, index) => ({
    ...metric,
    value: shared.metrics?.[index]?.value ?? '',
  }));
  next.profile.countries = (next.profile.countries || []).map((row, index) => ({
    ...row,
    percent: shared.countries?.[index]?.percent ?? 0,
  }));
  next.profile.cities = (next.profile.cities || []).map((row, index) => ({
    ...row,
    percent: shared.cities?.[index]?.percent ?? 0,
  }));
  next.formats.items = (next.formats.items || []).map((item, index) => ({
    ...item,
    muted: Boolean(shared.formats?.[index]?.muted),
  }));
  next.packages.items = (next.packages.items || []).map((plan, index) => ({
    ...plan,
    price: shared.packages?.[index]?.price ?? '',
    featured: Boolean(shared.packages?.[index]?.featured),
    cta_url: shared.packages?.[index]?.cta_url ?? '',
  }));
  next.process.steps = (next.process.steps || []).map((step, index) => ({
    ...step,
    number: shared.steps?.[index]?.number ?? '',
  }));
  next.contact.socials = (shared.socials || []).map((social) => ({
    platform: social.platform || 'other',
    url: social.url || '',
    label: social.label || '',
  }));
  return next;
}

function ensure(list, length, factory) {
  const next = Array.isArray(list) ? list.slice() : [];
  while (next.length < length) next.push(factory());
  return next;
}

function shape(lang) {
  const next = lang || {};
  next.seo = next.seo || {};
  next.hero = next.hero || {};
  next.audience = next.audience || {};
  next.audience.metrics = next.audience.metrics || [];
  next.profile = next.profile || {};
  next.profile.countries = next.profile.countries || [];
  next.profile.cities = next.profile.cities || [];
  next.profile.traveler = next.profile.traveler || {};
  next.profile.traveler.attributes = next.profile.traveler.attributes || [];
  next.formats = next.formats || {};
  next.formats.items = (next.formats.items || []).map((item) => ({
    ...item,
    specs: item?.specs || [],
  }));
  next.packages = next.packages || {};
  next.packages.items = (next.packages.items || []).map((item) => ({
    ...item,
    includes: item?.includes || [],
  }));
  next.process = next.process || {};
  next.process.steps = next.process.steps || [];
  next.faqs = next.faqs || {};
  next.faqs.items = next.faqs.items || [];
  next.contact = next.contact || {};
  next.contact.socials = next.contact.socials || [];
  return next;
}

function alignPair(left, right, factory) {
  const length = Math.max(left.length, right.length);
  return [ensure(left, length, factory), ensure(right, length, factory)];
}

function prepareKit(data) {
  const es = shape(hydrateLang(data?.es || {}));
  const en = shape(hydrateLang(data?.en || {}));
  const shared =
    data?.shared && typeof data.shared === 'object'
      ? JSON.parse(JSON.stringify(data.shared))
      : deriveShared(es, en);

  const metricLen = Math.max(shared.metrics?.length || 0, es.audience.metrics.length, en.audience.metrics.length);
  shared.metrics = ensure(shared.metrics, metricLen, () => ({ value: '' }));
  es.audience.metrics = ensure(es.audience.metrics, metricLen, () => ({ value: '', unit: '', label: '', note: '' }));
  en.audience.metrics = ensure(en.audience.metrics, metricLen, () => ({ value: '', unit: '', label: '', note: '' }));
  shared.metrics.forEach((row, index) => {
    const esSplit = splitMetric(es.audience.metrics[index].value);
    const enSplit = splitMetric(en.audience.metrics[index].value);
    if (typeof es.audience.metrics[index].unit !== 'string' || es.audience.metrics[index].unit === '') {
      es.audience.metrics[index].unit = esSplit.unit;
    }
    if (typeof en.audience.metrics[index].unit !== 'string' || en.audience.metrics[index].unit === '') {
      en.audience.metrics[index].unit = enSplit.unit;
    }
    const sharedSplit = splitMetric(row.value);
    row.value = sharedSplit.value === '' ? esSplit.value || enSplit.value || '' : sharedSplit.value;
  });

  const countryLen = Math.max(shared.countries?.length || 0, es.profile.countries.length, en.profile.countries.length);
  shared.countries = ensure(shared.countries, countryLen, () => ({ percent: 0 }));
  es.profile.countries = ensure(es.profile.countries, countryLen, () => ({ name: '', percent: 0 }));
  en.profile.countries = ensure(en.profile.countries, countryLen, () => ({ name: '', percent: 0 }));

  const cityLen = Math.max(shared.cities?.length || 0, es.profile.cities.length, en.profile.cities.length);
  shared.cities = ensure(shared.cities, cityLen, () => ({ percent: 0 }));
  es.profile.cities = ensure(es.profile.cities, cityLen, () => ({ name: '', percent: 0 }));
  en.profile.cities = ensure(en.profile.cities, cityLen, () => ({ name: '', percent: 0 }));

  const formatLen = Math.max(shared.formats?.length || 0, es.formats.items.length, en.formats.items.length);
  shared.formats = ensure(shared.formats, formatLen, () => ({ muted: false }));
  const blankFormat = () => ({ chip: '', title: '', description: '', muted: false, specs: [] });
  es.formats.items = ensure(es.formats.items, formatLen, blankFormat);
  en.formats.items = ensure(en.formats.items, formatLen, blankFormat);
  for (let index = 0; index < formatLen; index += 1) {
    const [esSpecs, enSpecs] = alignPair(
      es.formats.items[index].specs || [],
      en.formats.items[index].specs || [],
      () => ({ label: '', value: '' })
    );
    es.formats.items[index].specs = esSpecs;
    en.formats.items[index].specs = enSpecs;
  }

  const packageLen = Math.max(shared.packages?.length || 0, es.packages.items.length, en.packages.items.length);
  shared.packages = ensure(shared.packages, packageLen, () => ({ price: '', featured: false, cta_url: '' }));
  const blankPackage = () => ({
    name: '',
    price: '',
    period: '',
    description: '',
    featured: false,
    featured_label: '',
    cta_label: '',
    cta_url: '',
    includes: [],
  });
  es.packages.items = ensure(es.packages.items, packageLen, blankPackage);
  en.packages.items = ensure(en.packages.items, packageLen, blankPackage);
  for (let index = 0; index < packageLen; index += 1) {
    const [esLines, enLines] = alignPair(
      es.packages.items[index].includes || [],
      en.packages.items[index].includes || [],
      () => ''
    );
    es.packages.items[index].includes = esLines;
    en.packages.items[index].includes = enLines;
  }

  const stepLen = Math.max(shared.steps?.length || 0, es.process.steps.length, en.process.steps.length);
  shared.steps = ensure(shared.steps, stepLen, () => ({ number: '' }));
  const blankStep = () => ({ number: '', title: '', description: '' });
  es.process.steps = ensure(es.process.steps, stepLen, blankStep);
  en.process.steps = ensure(en.process.steps, stepLen, blankStep);

  const socialLen = Math.max(shared.socials?.length || 0, es.contact.socials.length, en.contact.socials.length);
  shared.socials = ensure(shared.socials, socialLen, () => ({ platform: 'other', label: '', url: '' }));
  const blankSocial = () => ({ platform: 'other', label: '', url: '' });
  es.contact.socials = ensure(es.contact.socials, socialLen, blankSocial);
  en.contact.socials = ensure(en.contact.socials, socialLen, blankSocial);
  shared.socials.forEach((row, index) => {
    if (!row.label) row.label = es.contact.socials[index].label || en.contact.socials[index].label || '';
    if (!row.url) row.url = es.contact.socials[index].url || en.contact.socials[index].url || '';
    if (!row.platform) row.platform = es.contact.socials[index].platform || 'other';
  });

  const [esAttrs, enAttrs] = alignPair(
    es.profile.traveler.attributes,
    en.profile.traveler.attributes,
    () => ({ label: '', value: '' })
  );
  es.profile.traveler.attributes = esAttrs;
  en.profile.traveler.attributes = enAttrs;

  const [esFaqs, enFaqs] = alignPair(es.faqs.items, en.faqs.items, () => ({
    question: '',
    answer: '',
    answer_html: '',
    answer_html_updated: '',
  }));
  es.faqs.items = esFaqs;
  en.faqs.items = enFaqs;

  shared.pdf_url = shared.pdf_url || es.hero.pdf_url || en.hero.pdf_url || '';
  shared.email = shared.email || es.contact.email || en.contact.email || '';
  shared.whatsapp = shared.whatsapp || es.contact.whatsapp || en.contact.whatsapp || '+52 686 400 2234';
  if (!es.contact.whatsapp_label || es.contact.whatsapp_label === 'o escríbenos por WhatsApp') {
    es.contact.whatsapp_label = 'También puedes escribirnos por WhatsApp';
  }
  if (!en.contact.whatsapp_label || en.contact.whatsapp_label === 'or message us on WhatsApp') {
    en.contact.whatsapp_label = 'You can also message us on WhatsApp';
  }
  if (!es.contact.whatsapp_kicker) es.contact.whatsapp_kicker = 'WHATSAPP';
  if (!en.contact.whatsapp_kicker) en.contact.whatsapp_kicker = 'WHATSAPP';

  return { shared, es, en };
}

export default function MediaKitManager() {
  const toast = useRef(null);
  const [kit, setKit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [fullPreview, setFullPreview] = useState(false);
  const [previewLang, setPreviewLang] = useState('es');
  const publicUrl = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL || '';
  const publicLinks = [
    { label: 'Español', href: `${publicUrl}${PUBLIC_PATHS.es}` },
    { label: 'English', href: `${publicUrl}${PUBLIC_PATHS.en}` },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/media_kit');
      setKit(prepareKit(res.data));
    } catch (err) {
      setKit(null);
      setError(err?.response?.data?.error || err.message || 'Could not load the media kit');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const persist = async () => {
    const payload = {
      shared: kit.shared,
      es: serializeLang(applyShared(kit.es, kit.shared)),
      en: serializeLang(applyShared(kit.en, kit.shared)),
    };
    const res = await api.put('/media_kit', payload);
    setKit(prepareKit(res.data));
  };

  const onSave = async () => {
    setSaving(true);
    try {
      await persist();
      toast.current?.show({ severity: 'success', summary: 'Saved', life: 2500 });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Save error',
        detail: err?.response?.data?.error || err.message,
        life: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  const onPublish = async () => {
    const secret = process.env.REACT_APP_BORDERGARITA_REVALIDATE_SECRET || '';
    if (!publicUrl || !secret) {
      toast.current?.show({
        severity: 'error',
        summary: 'Publish error',
        detail: 'Missing REACT_APP_BORDERGARITA_PUBLIC_APP_URL or REVALIDATE_SECRET',
        life: 5000,
      });
      return;
    }
    setPublishing(true);
    try {
      await persist();
      await Promise.all(
        [PUBLIC_PATHS.es, PUBLIC_PATHS.en].map((path) => revalidatePublicPath(publicUrl, secret, path))
      );
      toast.current?.show({ severity: 'success', summary: 'Published', life: 2500 });
    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Publish error',
        detail: err?.response?.data?.error || err.message,
        life: 5000,
      });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="p-3" style={{ paddingBottom: 120 }}>
      <Toast ref={toast} />
      <div className="flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
        <div>
          <h2 className="m-0">Media kit</h2>
          <p className="mt-2 mb-0 text-color-secondary" style={{ maxWidth: 720 }}>
            Prices, percentages, links and figures are entered once and published in both languages.
            The two columns are only the wording.
          </p>
        </div>
      </div>
      <div className="flex flex-wrap align-items-stretch gap-3 mb-4">
        {publicLinks.map((link) => (
          <a
            key={link.label}
            href={link.href}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              flex: '1 1 280px',
              minWidth: 0,
              padding: '12px 16px',
              border: '1px solid #e4e4ed',
              borderRadius: 12,
              background: '#fff',
              textDecoration: 'none',
              color: '#1a1a2e',
            }}
          >
            <strong>{link.label}</strong>
            <span style={{ color: '#2020a0', fontSize: 13, lineHeight: 1.4, wordBreak: 'break-all' }}>
              {link.href}
            </span>
          </a>
        ))}
        <Button
          type="button"
          label="Full preview"
          icon="pi pi-eye"
          className="p-button-outlined align-self-stretch"
          disabled={!kit}
          onClick={() => setFullPreview(true)}
        />
      </div>

      {error ? <Message severity="error" className="mb-3 w-full" text={error} /> : null}
      {loading ? <p>Loading media kit…</p> : null}
      {!loading && kit ? <MediaKitForm kit={kit} setKit={setKit} /> : null}

      <Dialog
        header="Full preview"
        visible={fullPreview}
        onHide={() => setFullPreview(false)}
        style={{ width: '96vw', maxWidth: 1100 }}
        maximizable
        blockScroll
        dismissableMask
        contentStyle={{
          background: '#f3f4f6',
          maxHeight: '82vh',
          overflow: 'auto',
        }}
      >
        <Dropdown
          value={previewLang}
          options={[
            { label: 'Español', value: 'es' },
            { label: 'English', value: 'en' },
          ]}
          onChange={(event) => setPreviewLang(event.value)}
          className="mb-3"
          style={{ minWidth: 180, marginTop: 12 }}
        />
        {kit ? <MediaKitSectionPreview section="all" kit={kit} lang={previewLang} /> : null}
      </Dialog>

      <div
        style={{
          position: 'fixed',
          bottom: 0,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '720px',
          maxWidth: '95vw',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '1rem',
          backgroundColor: 'white',
          boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
        }}
      >
        <Button
          type="button"
          label="Publish"
          icon="pi pi-cloud-upload"
          className="p-button-warning"
          loading={publishing}
          disabled={!kit || loading || saving}
          onClick={onPublish}
          style={{ width: '200px' }}
        />
        <Button
          type="button"
          label="Save"
          icon="pi pi-save"
          className="main-button"
          loading={saving}
          disabled={!kit || loading || publishing}
          onClick={onSave}
          style={{ width: '200px' }}
        />
      </div>
    </div>
  );
}
